import { createSelector } from "reselect";
import {
  deliveryMethodsSelector,
  defaultDeliveryModeSelector,
  sessionTokenSelector,
  userOrdersDeliveryDetailsSelector
} from "ducks/selectors";
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import {
  addressParams,
  deliverySlotParams,
  getChannelFromUrl,
  identityELOAParams,
  popStationParams
} from "utils/helpers";
import { ORDER_CHANNEL_MAPPING, SHIPMENT_METHOD } from "utils/constants";
import head from "lodash/head";

const deliveryInfoDomain = state =>
  get(state, "form.DeliveryInfoForm.values", {});

// const postOfficeDeliverySlotsDomain = state =>
//   get(state, "api.postOfficeDeliverySlots.data");

// ***************************************************************
// ************** deliveryInfoForm selectors ******************
// ***************************************************************

export const deliveryInfoSelector = createSelector(
  deliveryInfoDomain,
  deliveryInfo => deliveryInfo
);

export const isDeliveryInfoAvailable = createSelector(
  deliveryInfoDomain,
  deliveryInfo => !isEmpty(get(deliveryInfo, "values", {}))
);

export const channelSelector = () =>
  get(ORDER_CHANNEL_MAPPING, getChannelFromUrl());

export const selectedDeliveryMethodSelector = createSelector(
  defaultDeliveryModeSelector,
  deliveryInfoSelector,
  (defaultDeliveryMethod, deliveryInfo) => {
    return get(deliveryInfo, "delivery_method", defaultDeliveryMethod);
  }
);

export const selectedShippingMethodSelector = createSelector(
  deliveryInfoSelector,
  deliveryInfo => {
    return get(deliveryInfo, "shippingMethod");
  }
);

export const selectedSelfPickupSlotSelector = createSelector(
  deliveryInfoSelector,
  deliveryInfo => {
    return get(deliveryInfo, "selfPickUpLocation", {});
  }
);

export const deliveryNextButtonEnabled = createSelector(
  deliveryInfoSelector,
  deliveryInfo => {
    const {
      shippingMethod,
      delivery_address_line_1,
      delivery_province,
      delivery_city,
      delivery_area,
      delivery_slot_id,
      delivery_street_name,
      delivery_zip_code,
      delivery_slot_fee
    } = deliveryInfo;

    if (shippingMethod?.name === SHIPMENT_METHOD.SELF_PICKUP) {
      return !!deliveryInfo?.selfPickUpLocation;
    }
    const isDeliveryaddress =
      delivery_address_line_1 !== "" &&
      !!delivery_province?.value &&
      !!delivery_city?.value &&
      !!delivery_area?.value &&
      !!delivery_area?.postalCode &&
      delivery_slot_id;
    const isDeliveryStreetName =
      delivery_street_name !== "" &&
      !!delivery_city &&
      !isNaN(delivery_slot_fee + "") &&
      !!delivery_zip_code &&
      delivery_slot_id;

    if (shippingMethod?.name === SHIPMENT_METHOD.DOORSTEP_DELIVERY) {
      return isDeliveryaddress || isDeliveryStreetName;
    }
    return false;
  }
);

// ***************************************************************
// *************** deliveryInfoForm makeSelect *******************
// ***************************************************************

export const makeSelectDeliveryInfoForm = () => deliveryInfoSelector;

export const makeSelectDeliveryMethods = () => deliveryMethodsSelector;

export const makeSelectDeliveryMethod = () => selectedDeliveryMethodSelector;

export const makeSelectShippingMethod = () => selectedShippingMethodSelector;

export const makeSelectSelfPickupSlot = () => selectedSelfPickupSlotSelector;

export const makeSelectIsDeliveryInfoAvailable = () => isDeliveryInfoAvailable;

export const makeSelectDeliveryNextButtonEnabled = () =>
  deliveryNextButtonEnabled;

export const makeSelectScheduleDeliveryPayload = () =>
  createSelector(
    deliveryInfoSelector,
    sessionTokenSelector,
    userOrdersDeliveryDetailsSelector,
    (formValues, sessionToken, userOrder) => {
      if (isEmpty(formValues)) return {};
      const {
        alternate_number,
        order_type,
        delivery_type,
        token,
        fail_reason,
        receive_my_package,
        order_ref,
        delivery_method
      } = formValues;

      const { delivery_slot = {} } = deliverySlotParams(formValues);

      const payload = {
        order_type,
        delivery_type,
        token: token || sessionToken,
        order_ref,
        fail_reason: get(fail_reason, "value", ""),
        alternate_number:
          alternate_number ||
          get(formValues, "contact_number") ||
          get(head(userOrder), "contact_number"),
        ...(get(delivery_method, "action", "home") === "pop_station"
          ? popStationParams(formValues)
          : addressParams(formValues, "delivery")),
        ...delivery_slot,
        ...(receive_my_package ? identityELOAParams(formValues) : {})
      };

      return payload;
    }
  );
